import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { getAppList, type AppListItem } from '../../utils/app';
import { CommonUtils } from '../../utils/commonUtils';
import { formatDateFromApi } from '../../utils/dateTime';
import { Edit, Plus, Settings, Smartphone } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'all', label: '전체 상태' },
  { value: 'ACTIVE', label: '활성' },
  { value: 'INACTIVE', label: '비활성' },
  { value: 'PENDING', label: '대기중' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: '전체 타입' },
  { value: 'CUSTOMER', label: '고객용' },
  { value: 'AGENT', label: '에이전트' },
];

const AppList: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl } = useParams<{ projectCode: string }>();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('app-list');

  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [apps, setApps] = useState<AppListItem[]>([]);
  const [selectedApps, setSelectedApps] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  const handleSelectAll = () => {
    CommonUtils.handleSelectAll(selectedApps, filteredApps, setSelectedApps, (app) => app.code);
  };
  const handleSelectApp = (code: string) => {
    CommonUtils.handleSelectItem(code, selectedApps, setSelectedApps);
  };

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  // 앱 목록 조회 API
  useEffect(() => {
    if (!projectCodeFromUrl || !projectDetail) return;
    let cancelled = false;
    setError(null);
    setIsLoading(true);
    getAppList(projectCodeFromUrl)
      .then((res) => {
        if (!cancelled) setApps(res.apps ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '앱 목록을 불러오지 못했습니다.');
          setApps([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectCodeFromUrl, projectDetail]);

  const filteredApps = apps.filter((app) => {
    const matchStatus = selectedStatus === 'all' || app.status === selectedStatus;
    const matchType = selectedType === 'all' || app.type === selectedType;
    return matchStatus && matchType;
  });

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return formatDateFromApi(dateString) || dateString;
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; text: string }> = {
      ACTIVE: { bg: 'bg-green-100', text: 'text-green-800' },
      INACTIVE: { bg: 'bg-gray-100', text: 'text-gray-800' },
      PENDING: { bg: 'bg-amber-100', text: 'text-amber-800' },
    };
    const style = map[status] ?? { bg: 'bg-gray-100', text: 'text-gray-600' };
    const label = STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
    return (
      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${style.bg} ${style.text}`}>
        {label}
      </span>
    );
  };

  const getTypeLabel = (type: string) => TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="앱 설정"
          subtitle={projectCodeFromUrl ? `${projectDetail.name} (${projectCodeFromUrl})` : '앱 목록을 관리합니다'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="ml-2 text-red-800 underline"
                >
                  닫기
                </button>
              </div>
            )}
            {/* Filters and Actions - 상품목록과 동일 스타일 (키워드 없음, 리스트/그리드 버튼 없음) */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  {/* Status Filter */}
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>

                  {/* Type Filter (브랜드 셀렉트박스 → 타입 셀렉트박스) */}
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  {/* 앱 추가 버튼 */}
                  <button
                    type="button"
                    onClick={() => projectCodeFromUrl && navigate(`/projects/${projectCodeFromUrl}/apps/create`)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    앱 추가
                  </button>
                </div>
              </div>

              {/* Summary - 상품목록과 동일 */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  총 <span className="font-medium text-gray-900">{apps.length}</span>개 앱 /
                  <span className="ml-1">검색결과 <span className="font-medium text-gray-900">{filteredApps.length}</span>개</span>
                </p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1" />
                    활성: {apps.filter((a) => a.status === 'ACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-1" />
                    비활성: {apps.filter((a) => a.status === 'INACTIVE').length}
                  </span>
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-amber-400 rounded-full mr-1" />
                    대기중: {apps.filter((a) => a.status === 'PENDING').length}
                  </span>
                </div>
              </div>
            </div>

            {/* 목록 테이블 (페이징 없음) */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="pl-4 pr-0 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedApps.length === filteredApps.length && filteredApps.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="pl-6 pr-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        앱코드
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        상태
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        타입
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        이름
                      </th>
                      <th className="pl-6 pr-0 py-3 text-left">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          설정
                        </span>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        생성일
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">액션</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                          </div>
                        </td>
                      </tr>
                    ) : filteredApps.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                          <Smartphone className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">등록된 앱이 없습니다.</p>
                        </td>
                      </tr>
                    ) : (
                      filteredApps.map((app) => (
                        <tr key={app.code} className="hover:bg-gray-50">
                          <td className="pl-4 pr-0 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedApps.includes(app.code)}
                              onChange={() => handleSelectApp(app.code)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="pl-6 pr-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {app.code}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {getStatusBadge(app.status)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {getTypeLabel(app.type)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {app.name}
                          </td>
                          <td className="pl-6 pr-0 py-4 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() =>
                                projectCodeFromUrl &&
                                navigate(
                                  `/projects/${projectCodeFromUrl}/apps/${encodeURIComponent(app.code)}/config`
                                )
                              }
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center text-xs font-medium"
                              title="설정"
                            >
                              <Settings className="w-4 h-4 mr-1.5" />
                              설정
                            </button>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(app.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              type="button"
                              className="text-gray-400 hover:text-blue-600"
                              title="수정"
                              onClick={() =>
                                projectCodeFromUrl &&
                                navigate(`/projects/${projectCodeFromUrl}/apps/${encodeURIComponent(app.code)}/update`)
                              }
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Selected Actions Bar - 상품목록과 동일 */}
              {selectedApps.length > 0 && (
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      {selectedApps.length}개 앱 선택됨
                    </span>
                    <div className="flex items-center space-x-2">
                      <button type="button" className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
                        상태 변경
                      </button>
                      <button type="button" className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
                        엑셀 다운로드
                      </button>
                      <button type="button" className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700">
                        선택 삭제
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppList;